<script setup lang="ts">
  import type { EnumValues } from '@travelata-libs/utils';
  import { ref, computed } from 'vue';
  import { Floating, FloatingPlacement, FloatingTrigger } from '@/components';

  interface TooltipProps {
    width?: string;
    trigger?: EnumValues<typeof FloatingTrigger>;
    placement?: EnumValues<typeof FloatingPlacement>;
    visible?: boolean;
    zIndex?: number;
    disabled?: boolean;
  }

  interface TooltipEmits {
    (e: 'change-visible', value: boolean): void;
    (e: 'close'): void;
  }

  const props = withDefaults(defineProps<TooltipProps>(), {
    width: 'auto',
    trigger: FloatingTrigger.Click,
    placement: FloatingPlacement.Bottom,
    visible: false,
    zIndex: 5,
    disabled: false,
  });

  const emit = defineEmits<TooltipEmits>();

  const floatingElement = ref(null);

  const contentStyle = computed(() => {
    return {
      width: props.width,
      zIndex: props.zIndex,
    };
  });
</script>

<template>
  <Floating
    ref="floatingElement"
    class="tl-tooltip"
    :placement="placement"
    :trigger="trigger"
    :offset="8"
    :visible="visible"
    :disabled="disabled"
    @change-visible="emit('change-visible', $event)"
    @hide="emit('close')"
  >
    <template #reference>
      <slot name="reference" />
    </template>
    <template #floating>
      <div data-test-id="floating" class="tl-tooltip__floating" :style="contentStyle">
        <slot />
      </div>
    </template>
  </Floating>
</template>

<style lang="scss" scoped>
  .tl-tooltip {
    &__floating {
      background: var(--color-text-primary);
      border-radius: var(--rounding-s);
      box-shadow: var(--shadow-m);
      box-sizing: border-box;
      color: var(--color-text-inverse-white);
      max-width: 100vw;
      padding: var(--spacing-xxs) var(--spacing-xs);
      position: relative;
    }
  }
</style>
